import { useEffect, useState } from "react";
import closeButton from '../../assets/images/close.png'
import { createStaffCommittee, createStaffEmployee, getStaffCommittees, getStaffEmployees, updateStaffCommittee, updateStaffEmployee } from "../../services/AppService";
import { useNavigate } from "react-router-dom";
import MessageModal from "../../components/AdminComponents/Modals/MessageModal";
import ConfirmActionModal from "../../components/AdminComponents/Modals/ConfirmActionModal";
import AddStaffCommitteeForm from "../../components/dashboard/Prceedings/Committees/AddStaffCommitteeForm";
import ViewCommitteeDetailsModal from "../../components/dashboard/Prceedings/Committees/ViewCommitteeDetailsModal";
import { useTranslation } from "react-i18next";

const Committee = () => {
    const {t}=useTranslation();
    const navigate = useNavigate();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [committees, setCommittees] = useState([]);
    const [loading, setLoading] = useState(true);

    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedCommittee, setSelectedCommittee] = useState(null);
    const [editingCommittee, setEditingCommittee] = useState(null);

    const [confirmModal, setConfirmModal] = useState({
        open: false,
        formData: null,
    });

    const [messageModal, setMessageModal] = useState({
        open: false,
        type: "success",
        title: "",
        message: "",
    });

    const showSuccess = (msg) => {
        setMessageModal({
            open: true,
            type: "success",
            title: "Success",
            message: msg,
        });
    };

    const showError = (msg) => {
        setMessageModal({
            open: true,
            type: "error",
            title: "Error",
            message: msg,
        });
    };

    const handleFormSubmit = (formData) => {
        setIsModalOpen(false);
        setConfirmModal({
            open: true,
            formData: formData,
        });
    };

    const handleCancelConfirm = () => {
        setConfirmModal({ open: false, formData: null });
        setIsModalOpen(true);
    };

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            setDebouncedSearch(search);
        }, 500);
        return () => clearTimeout(delayDebounce);
    }, [search]);

    useEffect(() => {
        fetchCommittees();
    }, [page, size, debouncedSearch]);

    const fetchCommittees = async () => {
        try {
            setLoading(true);
            const response = await getStaffCommittees(debouncedSearch, page, size);

            if (response) {
                setCommittees(response.content || []);
                setTotalPages(response.totalPages || 0);
            }
        } catch (error) {
            console.error("Error fetching staff", error);
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmSave = async () => {
        const { formData } = confirmModal; // Now a plain JSON object { committeeId, membersId, active }
        try {
            if (editingCommittee) {
                // Use the committeeId as the unique ID for grouping updates
                await updateStaffCommittee(editingCommittee.committeeId, formData);
                showSuccess("Committee updated successfully");
            } else {
                await createStaffCommittee(formData);
                showSuccess("Committee members added successfully");
            }
            fetchCommittees();
            setConfirmModal({ open: false, formData: null });
            setEditingCommittee(null);
        } catch (error) {
            // Access structural error message from your new professional GlobalExceptionHandler
            const backendError = error.response?.data?.message || "Check backend logs";
            showError(backendError);
        }
    };


    const handleAdd = () => {
        setEditingCommittee(null);
        setIsModalOpen(true);
    };


    return (
        <div className="bg-white shadow-lg rounded-2xl p-6">
            {/* Header */}
            <div className="flex justify-between items-center border-b pb-3 mb-4">
                <h4 className="text-xl font-semibold text-blue">
                    {t('pageTitles.committeesList')}
                </h4>
                <img
                    src={closeButton}
                    alt="Close"
                    className="w-6 h-6 cursor-pointer"
                    onClick={() => navigate(-1)}
                />
            </div>

            <div className="text-end mb-2">
                <button className="btnAdd" onClick={handleAdd}>
                    {t('common.addNew')}
                </button>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
                <div className="flex items-center gap-2">
                    <label className="text-sm">{t('common.show')}:</label>
                    <select
                        value={size}
                        onChange={(e) => {
                            setSize(Number(e.target.value));
                            setPage(0);
                        }}
                        className="border rounded px-2 py-1 text-sm"
                    >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                    <label className="text-sm">{t('common.entries')}:</label>
                </div>
                <div>
                    <input
                        type="text"
                        placeholder={t('common.search')}
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(0);
                        }}
                        className="border rounded px-3 py-1 text-sm"
                    />
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                {loading ? (
                    <p className="text-center p-4"></p>
                ) : (
                    <table className="w-full border text-sm ctable rounded-lg">
                        <thead>
                            <tr className="bg-[#a0adf1] text-white">
                                <th className="px-3 py-2 text-center border-b border-black">{t('tables.srNo')}</th>
                                <th className="px-3 py-2 text-center border-b border-black">{t('tables.committee.committeeName')}</th>
                                <th className="px-3 py-2 text-center border-b border-black">{t('tables.edit')}</th>
                                <th className="px-3 py-2 text-center border-b border-black">{t('tables.view')}</th>
                                <th className="px-3 py-2 text-center border-b border-black">{t('tables.status')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {committees.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="text-center py-4">
                                        {t('forms.committee.noCommittees', 'No employees found')}
                                    </td>
                                </tr>
                            ) : (
                                committees.map((c, idx) => (
                                    <tr
                                        key={c.id}
                                        className={`${idx % 2 === 0 ? "bg-gray-50" : "bg-white"
                                            } hover:bg-gray-100`}
                                    >
                                        <td className="px-3 py-2 text-center">{page * size + idx + 1}</td>
                                        <td className="px-3 py-2">{c.committeeName}</td>
                                        <td className="px-3 py-2 text-center">
                                            <button className="px-3 py-1 btnImgEdit rounded text-xs"
                                                onClick={() => {
                                                    setEditingCommittee(c);
                                                    setIsModalOpen(true);
                                                }}
                                            ></button>
                                        </td>
                                        <td className="px-3 py-2 text-center">
                                            <button
                                                className="px-3 btnImgView py-1 rounded text-xs"
                                                onClick={() => {
                                                    setSelectedCommittee(c);
                                                    setViewModalOpen(true);
                                                }}
                                            ></button>
                                        </td>
                                        <td className="px-3 py-2 text-center">{c.active ? "Active" : "Inactive"}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-center mt-4 text-sm">
                <p>
                    {t('common.showing')} {page * size + 1} {" "}
                    {t('common.to')} {page * size + committees.length} {t('common.of')} {" "}
                    {totalPages * size} {t('common.entries')}
                </p>
                <div className="flex gap-2 mt-2 sm:mt-0">
                    <button
                        className="px-3 py-1 border rounded disabled:opacity-50"
                        onClick={() => setPage((p) => Math.max(p - 1, 0))}
                        disabled={page === 0}
                    >
                        {t('common.previous')}
                    </button>

                    {[...Array(totalPages)].map((_, i) => (
                        <button
                            key={i}
                            onClick={() => setPage(i)}
                            className={`px-3 py-1 border rounded ${page === i
                                ? "bg-gradient-to-b from-white to-[#dcdcdc] border border-[#979797]"
                                : ""
                                }`}
                        >
                            {i + 1}
                        </button>
                    ))}

                    <button
                        className="px-3 py-1 border rounded disabled:opacity-50"
                        onClick={() => setPage((p) => Math.min(p + 1, totalPages - 1))}
                        disabled={page === totalPages - 1}
                    >
                       {t('common.next')}
                    </button>
                </div>
            </div>

            <AddStaffCommitteeForm onSubmit={handleFormSubmit}
                editData={editingCommittee}
                open={isModalOpen}
                onClose={() => { setIsModalOpen(false); setEditingCommittee(null); }}
            />


            <ConfirmActionModal
                open={confirmModal.open}
                title={editingCommittee ? "Confirm Update" : "Confirm Addition"}
                message={
                    editingCommittee
                        ? "Are you sure you want to update this employee's details?"
                        : "Are you sure you want to add this new employee to the system?"
                }
                confirmText={editingCommittee ? "UPDATE" : "SAVE"}
                onConfirm={handleConfirmSave}
                onClose={handleCancelConfirm}
            />

            <MessageModal
                open={messageModal.open}
                onClose={() => setMessageModal({ ...messageModal, open: false })}
                type={messageModal.type}
                title={messageModal.title}
                message={messageModal.message}
            />

            <ViewCommitteeDetailsModal
                isOpen={viewModalOpen}
                onClose={() => setViewModalOpen(false)}
                committee={selectedCommittee}
            />

        </div>
    )
}

export default Committee;